import type { ReactNode } from 'react'

interface EntityPageHeaderProps {
  breadcrumb: ReactNode
  // Обычно строка; допускается ReactNode для inline-правки заголовка.
  title: ReactNode
  /** Полный заголовок для title-атрибута, когда `title` — ReactNode (inline-правка). */
  titleText?: string
  /** Бейдж рядом с h1 (приоритет и т.п.). */
  titleAccessory?: ReactNode
  /** Строка под заголовком (mono-адрес, версия документа). */
  subtitle?: ReactNode
  /** Первая meta-ячейка (лейбл + контент): статус живёт здесь, не рядом с h1. */
  status?: ReactNode
  /** Остальные meta-ячейки (<Meta/>). */
  meta?: ReactNode
  tags?: ReactNode
  actions?: ReactNode
  /** Хвост шапки: причина проигрыша, ссылки и прочий свободный контент. */
  extra?: ReactNode
}

// Шапка страницы сущности. All-slots и без данных: что показывать (и кому) —
// решает страница; здесь только раскладка.
export function EntityPageHeader({
  breadcrumb,
  title,
  titleText,
  titleAccessory,
  subtitle,
  status,
  meta,
  tags,
  actions,
  extra,
}: EntityPageHeaderProps) {
  // Длинное название не переносим на новые строки — обрезаем с многоточием;
  // полный текст — в title-атрибуте (строковый заголовок даёт его сам).
  const titleTooltip = typeof title === 'string' ? title : titleText
  return (
    <header className="flex flex-col gap-4">
      {breadcrumb}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1
              className="t-h2 min-w-0 truncate"
              {...(titleTooltip ? { title: titleTooltip } : {})}
            >
              {title}
            </h1>
            {titleAccessory}
          </div>
          {subtitle}
        </div>
        {actions != null && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>

      {(status != null || meta != null) && (
        <dl className="flex flex-wrap gap-x-8 gap-y-3">
          {status}
          {meta}
        </dl>
      )}

      {tags}
      {extra}
    </header>
  )
}

// Meta-ячейка шапки: лейбл + контент. Рендерится внутри <dl> (слоты status/meta).
export function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="label text-text-tertiary">{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}
